import { useEffect, useRef, useState } from 'react';
import type { IdentityRuntimeState } from '../runtime/session-state';
import type { PanelIdentityRuntime } from '../runtime/identity-runtime';

/** How close to the end of the window a renewal is allowed: the same two
 * minutes the focus/visibility path already uses. */
const RENEWAL_WINDOW_MS = 120_000;

/** True once the session's window has ended on the server's clock (`now`): it can no longer be renewed. */
const windowClosed = (expiresAt: string, now: number): boolean => Date.parse(expiresAt) <= now;

/** docs/decisions/2026-10-17-session-recheck-skipped-within-two-minutes.md: a
 * return to the Panel within two minutes of Identity's last confirmation of the
 * session checks nothing; the Backend still authorizes every request.
 * @internal Exported for the composition specs. */
export const SESSION_RECHECK_SKIP_MS = 120_000;

/** Foreground/reconnect revalidates Identity. The expiry timer only freezes
 * presentation, never asserts server authority.
 *
 * A return to the tab is one foreground event: the browser sends a visible
 * `visibilitychange` and a window `focus` for it, and the first one checks;
 * the next waits until the tab or the window has been left again.
 *
 * D-160 ruling 19: foreground *activity* renews too, inside that same last
 * window. There is still no background renewal — no timer renews anything, a
 * hidden tab renews nothing, and each window is renewed at most once. */
export function useIdentitySession(runtime: PanelIdentityRuntime, state: IdentityRuntimeState, pathname: string, hasUnsentWork: boolean) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [verifying, setVerifying] = useState(false);
  const verification = useRef(0);
  const [idleWarning, setIdleWarning] = useState(false);
  const renewedWindow = useRef<string | null>(null);
  const session = state.kind === 'credential_required' ? state.credential : 'session' in state ? state.session : null;
  const expiresAt = session?.canonical.session.expires_at;

  useEffect(() => { void runtime.rehydrateSession(); }, [runtime]);
  useEffect(() => {
    let mounted = true;
    // Left: the tab was hidden or the window lost the focus since the last foreground event.
    let away = true;
    // A return from a real disconnect that could not be checked yet (the tab was hidden): the next foreground checks.
    let owed = false;
    const verificationChanged = (pending: boolean) => {
      // Keep the port's dispatch fence synchronous with browser events; React
      // rendering (and DOM inert) alone cannot guard a captured account port.
      runtime.setInteractionFrozen(!navigator.onLine || pending, navigator.onLine ? 'verification' : 'offline');
      setVerifying(pending);
    };
    runtime.setInteractionFrozen(!navigator.onLine, 'offline');
    /** A return from a real disconnect (`owed`) is always checked. */
    const check = () => {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      const snapshot = runtime.getSnapshot();
      // A focus/reconnect must not erase an unconfirmed logout. Only its
      // explicit retry can establish whether the remote session was closed.
      if (snapshot.kind === 'signed_out' || snapshot.kind === 'membership_selection_required'
        || snapshot.kind === 'unavailable' && snapshot.operation === 'logout'
        || snapshot.kind === 'credential_required' && snapshot.status === 'unavailable' && snapshot.operation === 'logout') { owed = false; verificationChanged(false); return; }
      // A window that already closed while the expiry timer was frozen (iOS
      // suspends a background tab's timers) cannot be renewed: the server
      // refuses an expired bearer, so the expiry boundary appears at once.
      if (snapshot.kind === 'ready' && windowClosed(snapshot.session.canonical.session.expires_at, runtime.serverNow())) { owed = false; verificationChanged(false); runtime.expire(); return; }
      const renewal = snapshot.kind === 'ready' && Date.parse(snapshot.session.canonical.session.expires_at) - runtime.serverNow() <= RENEWAL_WINDOW_MS;
      // Nothing to confirm: no fence, no banner, no render.
      if (!owed && !renewal && runtime.sessionConfirmedWithin(SESSION_RECHECK_SKIP_MS)) return;
      owed = false;
      const generation = ++verification.current;
      verificationChanged(true);
      const request = renewal ? runtime.renew() : runtime.rehydrateSession();
      void request.finally(() => { if (mounted && generation === verification.current) verificationChanged(false); });
    };
    const foreground = () => {
      if (document.visibilityState === 'hidden') { away = true; return; }
      if (!away && !owed) return;
      away = false;
      check();
    };
    const leave = () => { away = true; };
    const reconnect = () => { owed = true; setOnline(true); verificationChanged(true); check(); };
    const disconnect = () => { verification.current += 1; setOnline(false); verificationChanged(false); };
    window.addEventListener('focus', foreground);
    window.addEventListener('blur', leave);
    document.addEventListener('visibilitychange', foreground);
    window.addEventListener('online', reconnect);
    window.addEventListener('offline', disconnect);
    return () => {
      mounted = false;
      verification.current += 1;
      runtime.setInteractionFrozen(true);
      window.removeEventListener('focus', foreground);
      window.removeEventListener('blur', leave);
      document.removeEventListener('visibilitychange', foreground);
      window.removeEventListener('online', reconnect);
      window.removeEventListener('offline', disconnect);
    };
  }, [runtime]);

  // The runtime owns the automatic re-check after an outage
  // (docs/decisions/2026-10-06-authority-unavailable-is-not-invalid.md); the
  // shell only watches it while mounted.
  useEffect(() => runtime.watchSessionRecovery(), [runtime]);

  useEffect(() => {
    if ((state.kind !== 'ready' && !(state.kind === 'credential_required' && state.status === 'required')) || !expiresAt) return undefined;
    const timeout = window.setTimeout(runtime.expire, Math.max(0, Date.parse(expiresAt) - runtime.serverNow()));
    return () => window.clearTimeout(timeout);
  }, [expiresAt, runtime, state.kind, state.kind === 'credential_required' ? state.status : null]);

  /* D-160 ruling 19 · QA row 31. Until this, a person who never left the tab
   * always met «Sigues aquí, pero hay que confirmar quién eres» after twenty
   * minutes, because only a focus or visibility change could reach
   * `runtime.renew()`. Working inside the tab is the same evidence of presence
   * that a refocus is, so it renews the same way, through the same operation.
   *
   * The three fences the ruling sets, in order of the guards below: the window
   * has to be in its last two minutes (never earlier, so this is not a keep-
   * alive), counted on the server's clock: the server renews only inside its
   * own last two minutes and answers the same token before them, so a device
   * whose clock ran ahead spent its window on that answer and then met the
   * boundary while its manager was still working; the tab has to be visible and online (a hidden tab renews nothing,
   * and nothing here is driven by a timer); and one window is renewed once —
   * `renewedWindow` holds the `expires_at` this effect already spent, and a
   * successful renewal replaces `expiresAt`, which re-runs the effect with a
   * fresh window. A failed renewal leaves the window as it was, so the guard
   * stays closed and the boundary still appears. */
  useEffect(() => {
    if (state.kind !== 'ready' || !expiresAt) return undefined;
    const renew = () => {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      if (renewedWindow.current === expiresAt) return;
      if (windowClosed(expiresAt, runtime.serverNow())) { runtime.expire(); return; }
      if (Date.parse(expiresAt) - runtime.serverNow() > RENEWAL_WINDOW_MS) return;
      renewedWindow.current = expiresAt;
      void runtime.renew();
    };
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    events.forEach((event) => window.addEventListener(event, renew, { passive: true, capture: true }));
    return () => events.forEach((event) => window.removeEventListener(event, renew, { capture: true }));
  }, [expiresAt, runtime, state.kind]);

  useEffect(() => {
    setIdleWarning(false);
    // Acceso _idleOk: Board only, never a seated screen or unsent work.
    // P-RT2 supplies the screen's draft signal through this explicit seam.
    if (state.kind !== 'ready' || pathname !== '/app/board' || hasUnsentWork) return undefined;
    let warning: number;
    let logout: number;
    const reset = () => {
      window.clearTimeout(warning); window.clearTimeout(logout); setIdleWarning(false);
      warning = window.setTimeout(() => {
        setIdleWarning(true);
        logout = window.setTimeout(() => { setIdleWarning(false); void runtime.logout(); }, 60_000);
      }, 900_000);
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'] as const;
    // Element scroll does not bubble (the main and sidebar scroll separately).
    events.forEach((event) => window.addEventListener(event, reset, { passive: true, capture: true }));
    reset();
    return () => {
      window.clearTimeout(warning); window.clearTimeout(logout);
      events.forEach((event) => window.removeEventListener(event, reset, { capture: true }));
    };
  }, [hasUnsentWork, pathname, runtime, state.kind]);

  return { online, verifying, idleWarning, stayActive: () => { setIdleWarning(false); void runtime.renew(); } };
}
